import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import axios from 'axios';
import { Holiday } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';

const Holidays: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/holidays`);
      setHolidays(response.data);
    } catch (error) {
      console.error('Erro ao carregar feriados:', error);
      setError('Erro ao carregar feriados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchHolidays();
    } else {
      setLoading(false);
    }
  }, [currentUser, fetchHolidays]);

  const handleDelete = async (holidayId: number, holidayName: string) => {
    const ok = await showConfirm({
      title: 'Excluir feriado',
      message: `Tem certeza que deseja excluir o feriado "${holidayName}"?`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/api/holidays/${holidayId}`);
      fetchHolidays();
    } catch (error: any) {
      console.error('Erro ao excluir feriado:', error);
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao excluir feriado',
        variant: 'danger',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/holidays`, {
        date,
        name,
      });
      fetchHolidays();
      setShowModal(false);
      setDate('');
      setName('');
    } catch (error: any) {
      console.error('Erro ao criar feriado:', error);
      setError(error.response?.data?.detail || 'Erro ao criar feriado');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  if (currentUser?.role !== 'admin') {
    return <div>Acesso negado. Apenas administradores podem gerenciar feriados.</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Feriados</h2>
        <Button variant="success" onClick={() => setShowModal(true)}>
          Novo Feriado
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {holidays.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum feriado cadastrado</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Nome</th>
                  <th>Cadastrado por</th>
                  <th>Data de Cadastro</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {holidays.map((holiday) => (
                  <tr key={holiday.id}>
                    <td>{formatDate(holiday.date)}</td>
                    <td>{holiday.name}</td>
                    <td>{holiday.creator?.name || 'N/A'}</td>
                    <td>{formatDate(holiday.created_at)}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(holiday.id, holiday.name)}
                      >
                        Excluir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        setDate('');
        setName('');
        setError(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Novo Feriado</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Data</Form.Label>
              <Form.Control
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nome do Feriado</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Natal, Ano Novo, etc."
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setDate('');
                setName('');
                setError(null);
              }}
            >
              Cancelar
            </Button>
            <Button variant="success" type="submit" disabled={submitting}>
              {submitting ? 'Salvando...' : 'Salvar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Holidays;
